import { useContext, useEffect, useMemo, useReducer, useState } from 'react'
import { WppButton, WppDivider, WppIconAdd, WppTypography } from '@wppopen/components-library-react'
import appContext from 'context/appContext'
import {
  IdValuePair,
  SelectFilterComponentData,
  SelectFilterOptionPayload,
  SetSortByPayload,
} from 'reducers/GenericFiltersReducer/GenericFiltersReducer.d'
import { getFilterElementInitFields } from '../Home/utils'
import {
  filterReducer,
  getFilterReducerEmptyState,
  initialiseFilterOptions,
  removeAllFilters,
  selectFilterOption,
  setAreDependantFieldsReady,
  setIsPreselectComplete,
  setSortBy,
} from 'reducers/GenericFiltersReducer/GenericFiltersReducer'
import Filters from 'components/Filters/Filters'
import type {
  SortOrder,
  ProjectsListParams,
  ProjectsListData,
  ProjectsCreateBody,
  ProjectsArchiveCreateData,
} from '@contracts'
import {
  combineSearchParams,
  createCsApiUrlFromEndpoint,
  crossReferenceArray,
  getDefaultPaginationSearchParams,
  getTypeKey,
  updateSort,
} from 'utils'
import Banner from 'components/Banner'
import { CreateAndEditNewProject, GenericModal } from 'components/Modals'
import useApi from 'hooks/useApi'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { RowClickedEvent } from 'ag-grid-community'
import Table from 'components/table'
import { SortingDetails } from 'components/table/table-types'
import useDependantApi from 'hooks/useDependantApi'
import { projectTableFilters } from './utils'
import { refreshProjects, handleLoadMore, projectsColumnStructure, SingleProject } from 'pages/Home/utils-ProjectView'
import {
  paginatedTableReducer,
  getPaginatedTableReducerEmptyState,
  setRowData,
  setTaskPosition,
  setTotalItemCount,
  setPosition,
} from 'reducers/PaginatedTableReducer/PaginatedTableReducer'
import { DEFAULT_API_ITEM_LIMITS, FILTER_IDS, PDP_GENERATOR_API_HEADERS, SEARCH_PARAMETERS } from 'utils/constants'
import TableMetaDataRow from 'components/table/TableMetaDataRow'
import usePermissions from 'hooks/usePermissions'
import useToast from 'hooks/useToast'
import { defaultFieldsSettings, OnSubmitGenericModalProps } from 'components/Modals/GenericModal/helper'
import type { ClientsListDataV5, singleBrandClientV5, singleClientV5 } from 'utils/utils.d'
import type { FEApiError } from 'utils/errorHandling'
import homePageStyles from '../Home/HomePage.module.scss'
import styles from './ProjectManagerPage.module.scss'
import useInitialiser from 'hooks/useInitialiser'

const { DELETE_PROJECT } = SEARCH_PARAMETERS
const { PROJECTS_API } = DEFAULT_API_ITEM_LIMITS
const { PROJECT_MANAGER } = FILTER_IDS
const { CLIENT_IDS_HEADER } = PDP_GENERATOR_API_HEADERS

function ProjectManagerPage() {
  const navigate = useNavigate()
  const {
    importedClients,
    preservedFilterSelection,
    setNewProjectIdForBanner,
    updatePreservedFilterSelectionWithReducer,
    configApi,
  } = useContext(appContext)
  const { toastDetails, setToastDetails } = useToast()
  const { data: configData } = configApi
  const [searchParams, setSearchParams] = useSearchParams()
  const deletedProjectName = searchParams.get(DELETE_PROJECT) || undefined
  // if it has project details to delete, it will show the confirmation modal
  const [projectDetailsToDelete, setProjectDetailsToDelete] = useState<SingleProject>()
  const [isCreateNewProjectActive, setIsCreateNewProjectActive] = useState(false)
  // The locally controlled state for the current clients, needs to be kept in sync with the preserved filter state
  const [currentClients, setCurrentClients] = useState<ClientsListDataV5['clients'] | null>(null)

  // set the banner details
  useEffect(() => {
    if (deletedProjectName) {
      setToastDetails({
        message: `Project "${deletedProjectName}" deleted successfully.`,
        type: 'success',
      })

      // remove the deleted_project query param from the URL if present
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete(DELETE_PROJECT)
      setSearchParams(newSearchParams)
    }
  }, [deletedProjectName])

  // have the filters been initialised (populated with options from API data and preselected filters from global context)
  const [isPageInitialised, setIsPageInitialised] = useState<boolean>(false)
  // get projects api
  const getProjectsApiMethods = useApi<ProjectsListData>('v2/projects')
  const {
    data: getProjectsData,
    isLoading: isGetProjectsLoading,
    error: getProjectsError,
    url: getProjectsUrl,
    fetchData: getProjects,
    setUrl: setGetProjectsUrl,
  } = getProjectsApiMethods

  // create new project api
  const {
    fetchData: postProjectV2,
    url: postProjectData,
    isLoading: isPostProjectLoading,
  } = useApi<ProjectsCreateBody>('v2/projects')

  // archive/delete project
  const {
    fetchData: fetchArchiveProject,
    setUrl: setArchiveProjectUrl,
    url: archiveSingleProjectUrl,
    isLoading: archiveProjectIsLoading,
  } = useApi<ProjectsArchiveCreateData>('')

  const { isInitialising } = useInitialiser({ subscribedApis: [getProjectsApiMethods] })

  const { clientsWithPermissions } = usePermissions({
    permissionSubset: [
      'PDP_APP_GET_PROJECTS',
      'PDP_APP_GET_V2_PROJECTS',
      'PDP_APP_POST_V2_PROJECTS',
      'PDP_APP_POST_PROJECTS_BY_ARCHIVE',
    ],
  })

  const [clientsWithGetLegacyProjects, clientsWithGetV2Projects, clientsWithPostV2Project, clientsWithArchiveProject] =
    clientsWithPermissions

  const clientsWithGetProjects = clientsWithGetLegacyProjects || clientsWithGetV2Projects

  // delete project set the url
  useEffect(() => {
    if (!projectDetailsToDelete) return
    const { _id } = projectDetailsToDelete
    setArchiveProjectUrl(createCsApiUrlFromEndpoint(`v2/projects/${_id}/archive`))
  }, [projectDetailsToDelete])

  // completed projects table state
  const [shouldProjectDataAppend, setShouldProjectDataAppend] = useState(false)
  const [projectsTableReducerState, projectsTableReducerDispatch] = useReducer(
    paginatedTableReducer,
    getPaginatedTableReducerEmptyState(),
  )

  const projectsFilterReducer = useReducer(
    filterReducer,
    getFilterReducerEmptyState(PROJECT_MANAGER, projectTableFilters),
  )
  // "search by project title" filter reducer
  const [projectsFilterReducerState, projectsFilterReducerDispatch] = projectsFilterReducer
  const preservedFilterObjects = preservedFilterSelection[projectsFilterReducerState.reducerId]
  const preservedClientFilters = preservedFilterObjects?.find(({ filterId }) => filterId === 'clients') || null
  const preservedBrandFilters = preservedFilterObjects?.find(({ filterId }) => filterId === 'brands')
  const preservedRegionFilters = preservedFilterObjects?.find(({ filterId }) => filterId === 'regions') || null

  const activeClientFilterString = JSON.stringify(projectsFilterReducerState.index['clients'].activeFilters)
  const preservedClientFilterString = JSON.stringify(preservedClientFilters?.activeFilters)

  // ############################## FILTER LOGIC ##############################

  // initialise filters with client data after the clients API has returned
  useEffect(() => {
    if (!importedClients?.length) return

    // Get the data for initialising the client filter ELEMENT
    const { currentFilterComponent: clientFilterComponent } = getFilterElementInitFields<singleClientV5>(
      'client_name',
      importedClients,
      preservedClientFilters,
    )

    // when revisiting the page, maybe the api response may have changed, so only select the preserved clients if they are still in the API response
    const nonStalePreservedClients = preservedClientFilters
      ? crossReferenceArray(importedClients, preservedClientFilters?.activeFilters as IdValuePair[])
      : []

    // update the filter state with the preserved client data (pre-selecting previously selected options)
    projectsFilterReducerDispatch(initialiseFilterOptions('clients', clientFilterComponent))

    if (!nonStalePreservedClients.length) {
      projectsFilterReducerDispatch(setIsPreselectComplete(true))
      return
    }

    // Get the initialisation data for the brands filter element from the data received in the clients API response
    // and any preserved brands selected from previous interactions on the page
    const { currentFilterComponent: brandFilterComponent } = getFilterElementInitFields<singleBrandClientV5>(
      'brand_name',
      nonStalePreservedClients
        .map(({ brands }) => brands)
        .filter((brands): brands is NonNullable<typeof brands> => Boolean(brands))
        .flat(),
      preservedBrandFilters!,
    )

    // Iniitialise the brands filter reducer
    projectsFilterReducerDispatch(initialiseFilterOptions('brands', brandFilterComponent))
    projectsFilterReducerDispatch(setIsPreselectComplete(true))
  }, [importedClients])

  // Set the current clients in reducer state
  useEffect(() => {
    // If no clients data is received from the API, exit
    if (!importedClients) return

    projectsFilterReducerDispatch(setAreDependantFieldsReady(false))

    const clientFilterElementState = projectsFilterReducerState.index['clients'] as SelectFilterComponentData

    // If the user has manually selected a client, use the existing filter state, otherwise use the preserved filter state to preselect any clients
    if (clientFilterElementState?.activeFilters?.length) {
      // if active filters for client (e.g. selecting client in select box)
      setCurrentClients(crossReferenceArray(importedClients, clientFilterElementState.activeFilters as IdValuePair[]))
    } else {
      // set client from filter preservation (e.g. navigating away and back), or fallback to first client in list (e.g. first load)
      setCurrentClients(
        crossReferenceArray(importedClients, (preservedClientFilters?.activeFilters as IdValuePair[]) || []),
      )
    }
  }, [activeClientFilterString, preservedClientFilterString])

  // / initialise filters with regions data after the config API has returned
  useEffect(() => {
    if (!configData?.regions) return

    const regions: IdValuePair[] = configData?.regions.map(
      ({ _id, name }) =>
        ({
          id: _id,
          value: name,
        }) as IdValuePair,
    )

    // Get the data for initialising the region & retailer filter ELEMENTs
    const { currentFilterComponent: regionFilterComponent } = getFilterElementInitFields<IdValuePair>(
      getTypeKey<IdValuePair>('value'),
      regions,
      preservedRegionFilters,
    )

    projectsFilterReducerDispatch(initialiseFilterOptions('region_ids', regionFilterComponent))
  }, [configData?.regions])

  // remove all filters & update brands options on client change
  useEffect(() => {
    if (!importedClients) return

    const { currentFilterComponent: brandFilterComponent } = getFilterElementInitFields<singleBrandClientV5>(
      'brand_name',
      currentClients?.length
        ? currentClients.flatMap(({ brands }) => brands).filter((brand): brand is NonNullable<typeof brand> => !!brand)
        : importedClients
            ?.flatMap(({ brands }) => brands)
            .filter((brand): brand is NonNullable<typeof brand> => !!brand) || [],
      preservedBrandFilters!,
    )

    // Initialise the new 'brand' select filter options after a change in client
    projectsFilterReducerDispatch(initialiseFilterOptions('brands', brandFilterComponent))
    let shouldClearBrands = false

    if (isPageInitialised && !projectsFilterReducerState.areDependantFieldsReady) {
      // This should remove all of the active filters for brands
      projectsFilterReducerDispatch(removeAllFilters('brands'))
      updatePreservedFilterSelectionWithReducer(projectsFilterReducerState)
      shouldClearBrands = !!brandFilterComponent.activeFilters.length
    } else {
      setIsPageInitialised(true)
    }

    // dependant fields have updated, globalFilterReducer is ready
    if (!shouldClearBrands) projectsFilterReducerDispatch(setAreDependantFieldsReady(true))
  }, [importedClients, currentClients, JSON.stringify(preservedClientFilters?.activeFilters)])

  // Update the filter selection in the context for persistance of filters accross different pages
  useEffect(() => {
    if (isPageInitialised && projectsFilterReducerState.areDependantFieldsReady) {
      updatePreservedFilterSelectionWithReducer(projectsFilterReducerState)
    }
  }, [projectsFilterReducerState.urlQueryParams])

  // pre-select filters from the preserved filter state (excluding clients as that pre-selection is handled above)
  useEffect(() => {
    preservedFilterObjects
      // clients and brands preselection are handled above
      ?.filter(({ filterId }) => filterId !== 'clients' && filterId !== 'brands')
      .forEach(selectedFilter => {
        const matchedObject = projectsFilterReducerState.index[selectedFilter.filterId]
        if (!matchedObject) return
        // if the filter type found in the preserved filter state matches a filter type on the page, pre-select the filter
        projectsFilterReducerDispatch(
          selectFilterOption(matchedObject.type, {
            filterObject: matchedObject,
            newFilterValues: selectedFilter.activeFilters,
          } as SelectFilterOptionPayload),
        )
      })
    projectsFilterReducerDispatch(setIsPreselectComplete(true))
  }, [])

  // pre-select filters from the preserved filter state (excluding clients as that pre-selection is handled above)
  useEffect(() => {
    if (!isPageInitialised) return

    preservedFilterSelection[projectsFilterReducerState.reducerId]?.forEach(selectedFilter => {
      const matchedObject = projectsFilterReducerState.index[selectedFilter.filterId]
      if (!matchedObject) return
      projectsFilterReducerDispatch(
        selectFilterOption(matchedObject.type, {
          filterObject: matchedObject,
          newFilterValues: selectedFilter.activeFilters,
        } as SelectFilterOptionPayload),
      )
    })
    projectsFilterReducerDispatch(setIsPreselectComplete(true))
  }, [isPageInitialised])

  // create the query parameters from filter reducers
  const projectApiSearchParams: URLSearchParams = useMemo(() => {
    const defaultPaginationSearchParams = getDefaultPaginationSearchParams(PROJECTS_API)
    const apiUrlSearchParams = combineSearchParams(
      defaultPaginationSearchParams,
      projectsFilterReducerState.urlQueryParams,
    )

    return apiUrlSearchParams
  }, [projectsFilterReducerState.urlQueryParams])

  useDependantApi({
    apiMethods: getProjectsApiMethods,
    parentApiSearchParams: projectApiSearchParams,
    defaultQueryParams: projectsFilterReducerState.index['statuses'].activeFilters.length
      ? {}
      : {
          statuses: ['draft', 'in_progress', 'completed'].join(','),
        },
    hasPreselectionCompleted: projectsFilterReducerState.isPreselectComplete,
    areReducerDependenciesResolved: projectsFilterReducerState.areDependantFieldsReady,
  })
  // ############################## FILTER LOGIC END ##############################

  // ############################## TABLE LOGIC ##############################
  // update table data with load more
  // this will be the function that will process the data from the api
  const processProjectData = () => {
    if (!getProjectsData) return
    const { projects, position, item_count, total_item_count } = getProjectsData

    const projectsTableReducerState = projects?.map(projectDetails => {
      // find the client and brand name from the client data
      const clientDetails = importedClients?.find(client => client.id === projectDetails.client_id)
      const brandDetails = clientDetails?.brands?.find(brand => brand.id === projectDetails.brand_id)

      return {
        ...projectDetails,
        client_name: clientDetails?.client_name || '',
        brand_name: brandDetails?.brand_name || '',
      }
    })

    if (!projectsTableReducerState) return
    projectsTableReducerDispatch(
      setRowData({
        rowData: clientsWithGetProjects?.length ? projectsTableReducerState : [],
        shouldAppend: shouldProjectDataAppend,
      }),
    )
    projectsTableReducerDispatch(setTaskPosition(shouldProjectDataAppend ? position! + item_count! : 0))
    projectsTableReducerDispatch(setTotalItemCount(total_item_count!))
  }

  // handle API response
  useEffect(() => {
    if (isGetProjectsLoading || !getProjectsData || getProjectsError) return
    processProjectData()
    setShouldProjectDataAppend(false)
  }, [isGetProjectsLoading])
  // end of load more functionality

  // update table with sorting
  const handleCompletedTableSorting = (sortData: SortingDetails | undefined) => {
    const resetPosition = 0
    projectsFilterReducerDispatch(
      setSortBy(
        sortData
          ? {
              column: sortData!.column as NonNullable<ProjectsListParams['sort_by']>,
              sort: (sortData!.sort ? sortData!.sort + 'ending' : null) as SortOrder,
            }
          : null,
      ),
    )

    const sortDataPayload = sortData
      ? ({
          column: sortData.column,
          sort: `${sortData.sort}ending`,
        } as SetSortByPayload<NonNullable<ProjectsListParams['sort_by']>>)
      : null

    const urlSorting = updateSort(getProjectsUrl, sortDataPayload)

    projectsTableReducerDispatch(setTaskPosition(resetPosition))
    setPosition(urlSorting, setGetProjectsUrl, resetPosition)
    setShouldProjectDataAppend(false)
  }

  const refreshCompletedProjects = () => {
    refreshProjects(projectsTableReducerState.rowData?.length, getProjectsUrl, () => {}, getProjects)
  }

  const handleRowClicked = (e: RowClickedEvent) => {
    const target = e.event?.target as HTMLElement
    if (target.tagName.match(/WPP-(ICON-MORE|ACTION-BUTTON|ICON-BUTTON|AVATAR)/)) return
    const { _id } = e.data
    // otherwise it should go to the task view
    navigate(`/project/${_id}`)
  }
  // ############################## TABLE LOGIC END ##############################

  //  ############################## CREATE NEW PROJECT LOGIC ##############################
  const handleCreateNewProject = async (data: ProjectsCreateBody) => {
    try {
      const response = await postProjectV2(postProjectData, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: { [CLIENT_IDS_HEADER]: data.client_id as string },
      })
      const { _id } = response
      if (!_id) throw new Error('No project ID returned from the API')
      setNewProjectIdForBanner(_id)
      navigate(`/project/${_id}`)
    } catch (err) {
      throw err
    }
  }
  //  ############################## CREATE NEW PROJECT LOGIC END ##############################

  const handleDeleteProject = async (data: OnSubmitGenericModalProps) => {
    if (!projectDetailsToDelete) return

    try {
      await fetchArchiveProject(archiveSingleProjectUrl, {
        method: 'POST',
        body: JSON.stringify({
          update_info: data,
        }),
      })

      setToastDetails({
        message: `Project "${projectDetailsToDelete.project_name}" deleted successfully.`,
        type: 'success',
      })
      refreshCompletedProjects()
      setProjectDetailsToDelete(undefined)
    } catch (err) {
      const error = err as FEApiError
      const errMessage = error.errors?.[0].message || error.message || 'Something went wrong'
      throw new Error(errMessage)
    }
  }

  return (
    <>
      <div className={styles.header_section}>
        <WppTypography type="xl-heading" tag="h1">
          Projects
        </WppTypography>

        {!!clientsWithPostV2Project?.length ? (
          <WppButton
            onClick={() => setIsCreateNewProjectActive(true)}
            data-testid="create-new-project-button"
            disabled={isInitialising}
          >
            <WppIconAdd slot="icon-start" />
            Create New Project
          </WppButton>
        ) : null}

        <Banner
          text={toastDetails?.message}
          type={toastDetails?.type}
          onBannerClick={toastDetails?.onBannerClick}
          shouldBannerShow={!!toastDetails}
          onClose={() => setToastDetails(undefined)}
        />
      </div>

      <header className={homePageStyles.header__container}>
        <Filters
          reducerState={projectsFilterReducerState}
          reducerDispatch={projectsFilterReducerDispatch}
          isTargetApiLoading={isGetProjectsLoading}
          shouldDisplayPills
          filtersId="projects"
          permissions={isInitialising ? null : { canGetEntity: !!clientsWithGetProjects?.length }}
          initialisation={{
            isInitialising,
          }}
        />
      </header>
      {(isInitialising || !!clientsWithGetProjects?.length) && <WppDivider />}

      <section>
        {!!clientsWithGetProjects?.length && (
          <TableMetaDataRow
            text={`Showing ${projectsTableReducerState.itemCount || 0} results of ${getProjectsData?.total_item_count || 0}`}
            refreshHandler={refreshCompletedProjects}
            isRefreshRHS
            isInitialising={isInitialising}
          />
        )}

        {projectsTableReducerState.rowData?.length || isInitialising ? (
          /* if is not loading, there is data (meaning api was called) and there is no projects projects */
          <Table
            data-testid="projects-table"
            loading={isGetProjectsLoading}
            loadedRows={projectsTableReducerState.itemCount}
            totalRows={projectsTableReducerState.totalItemCount}
            rowData={projectsTableReducerState.rowData}
            columnDefs={projectsColumnStructure(
              configData?.regions,
              setProjectDetailsToDelete,
              clientsWithArchiveProject,
            )}
            onLoadMore={() =>
              handleLoadMore(
                getProjectsData!,
                projectsTableReducerDispatch,
                setGetProjectsUrl,
                getProjectsUrl,
                setShouldProjectDataAppend,
              )
            }
            onSortingUpdate={handleCompletedTableSorting}
            onRowClicked={handleRowClicked}
            isDrillable
            isInitialising={isInitialising}
          />
        ) : (
          /* if is not loading, here is data (meaning api was called) and there is projects */
          <div className={homePageStyles.no_table_content}>
            <WppTypography tag="p" type="s-body" data-testid="no-projects-message">
              {getProjectsError
                ? 'Something went wrong on our side, try again later'
                : 'There are currently no projects available to view'}
            </WppTypography>
          </div>
        )}
      </section>

      {!!projectDetailsToDelete && (
        <GenericModal
          onCancel={() => setProjectDetailsToDelete(undefined)}
          modalTitle="Delete project"
          submitButtonText="Delete"
          onSubmit={handleDeleteProject}
          shouldModalOpen={!!projectDetailsToDelete}
          isApiLoading={archiveProjectIsLoading}
          fieldsConfig={{
            ...defaultFieldsSettings,
            hasCommentField: true,
            isCommentRequiredField: false,
          }}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-disclaimer">
            Are you sure you want to delete this project?
            <br />
            After you delete a project this cannot be undone.
          </WppTypography>
        </GenericModal>
      )}

      {!!clientsWithPostV2Project?.length && isCreateNewProjectActive && (
        <CreateAndEditNewProject
          isModalOpen={isCreateNewProjectActive}
          isApiLoading={isPostProjectLoading}
          onCancel={() => setIsCreateNewProjectActive(false)}
          onSubmit={handleCreateNewProject}
        />
      )}
    </>
  )
}

export default ProjectManagerPage
